/* eslint-disable @typescript-eslint/no-explicit-any */
/** @format */

import { useAuthStore } from "../../features/auth/store";
import { useTenantStore } from "../stores/useTenantStore";
import { getOspreyApiBaseUrl } from "./apiBase";

// Endpoints that need a merchant/tenant context appended as a query param.
//
// ⚠️ TWO DIFFERENT PARAM NAMES, because osprey-api serves these paths two different ways
// (see osprey-api storefront-admin-proxy.controller.ts):
//
//   • RAW `@All()` PROXY TUNNELS to rack-api — rack-api's own contract names the param
//     `merchantId` (or an `x-merchant-id` header) and its DTOs use forbidNonWhitelisted,
//     so an unexpected `tenantId` is rejected outright with
//     422 `property tenantId should not exist`. Verified live on development 2026-09-02.
//   • osprey-api-OWNED controllers — these accept `tenantId`.
//
// Sending `tenantId` to the raw-proxied group broke the Product Catalogue and the
// colours/sizes/materials/size-guides metadata tabs for any pinned merchant: every list
// 422'd. Keep each path in the group that matches how osprey-api serves it.
const MERCHANT_SCOPED_PREFIXES = [
  "/products",
  "/colours",
  "/sizes",
  "/materials",
  "/size-guides",
];

const TENANT_SCOPED_PREFIXES = [
  "/orders",
  "/categories",
  "/dashboard",
  // No such routes on osprey-api today — both 404 regardless of the param. Left here so a
  // future implementation inherits tenant scoping rather than silently going unscoped.
  "/inventory",
  "/slider-mapping",
];

const matchesPrefix = (endpoint: string, prefixes: string[]) =>
  prefixes.some((prefix) => {
    const path = endpoint.split('?')[0];
    return path === prefix || path.startsWith(prefix + '/');
  });

/** The query param name this endpoint's owner expects, or null when it takes neither. */
const merchantScopeParam = (endpoint: string): 'merchantId' | 'tenantId' | null => {
  if (matchesPrefix(endpoint, MERCHANT_SCOPED_PREFIXES)) return 'merchantId';
  if (matchesPrefix(endpoint, TENANT_SCOPED_PREFIXES)) return 'tenantId';
  return null;
};

// Toast notification function
const showToast = (message: string, type: 'error' | 'success' = 'error') => {
  const toast = document.createElement('div');
  toast.className = 'fixed top-4 right-4 z-50 px-4 py-2 rounded-md text-white font-medium ' +
    (type === 'error' ? 'bg-red-500' : 'bg-green-500') +
    ' animate-in slide-in-from-top-2 duration-300';
  toast.textContent = message;
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.remove();
  }, 5000);
};

// osprey-api BFF base — the single backend origin the UI talks to. See apiBase.ts.
const getBaseUrl = getOspreyApiBaseUrl;

const getAdminApiKey = (): string => {
  return (window as any).__RUNTIME_CONFIG__?.ADMIN_API_KEY
    ?? import.meta.env.VITE_ADMIN_API_KEY
    ?? '';
};

/**
 * Auth + tenant context shared by every call to the BFF, JSON or file.
 *
 * Returns the headers AND the (possibly rewritten) endpoint, because the
 * tenant-scoped prefixes append a `tenantId` query param rather than a header.
 * `accept` differs per caller: JSON requests ask for JSON, downloads accept
 * anything so the server can pick CSV vs XLSX.
 */
function buildRequest(
  endpoint: string,
  accept: string,
): { url: string; headers: Record<string, string> } {
  const headers: Record<string, string> = { Accept: accept };

  const adminApiKey = getAdminApiKey();
  if (adminApiKey) {
    headers["x-api-key"] = adminApiKey;
  }

  const { token, selectedTenantId } = useAuthStore.getState();
  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }
  if (selectedTenantId) {
    headers["x-tenant-id"] = selectedTenantId;
  }

  // Append the merchant context only for endpoints that take one, under the name their
  // owner expects (`merchantId` for rack-api raw proxies, `tenantId` for osprey-api's own).
  const scopeParam = merchantScopeParam(endpoint);
  if (scopeParam) {
    const activeMerchant = useTenantStore.getState().activeMerchant;
    if (activeMerchant?.id) {
      const separator = endpoint.includes('?') ? '&' : '?';
      endpoint = endpoint + separator + scopeParam + '=' + activeMerchant.id;
    }
  }

  return { url: getBaseUrl() + endpoint, headers };
}

/**
 * Shared non-2xx handling: 401 logs out and redirects, errorCode 1600 opens the
 * merchant selector instead of toasting, everything else toasts. Always throws.
 *
 * The thrown object carries `toasted` so a caller can tell "the user has already
 * been told" from a failure that never reached here at all (a network-level
 * rejection, or a body read that blew up) and needs its own message.
 */
async function throwResponseError(res: Response, fallbackMessage: string): Promise<never> {
  // A file route still returns JSON on error, but a proxy/gateway failure may not.
  let errorData: any = {};
  try {
    errorData = await res.json();
  } catch {
    errorData = {};
  }

  if (res.status === 401) {
    const { logout } = useAuthStore.getState();
    logout();
    showToast('Session expired. Please login again.');
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = "/login?redirect_to=" + encodeURIComponent(currentPath);
    }
  }

  const errorMessage =
    errorData.message || errorData.errors?.[0]?.message || res.statusText || fallbackMessage;
  const isMerchantRequired = errorData.errorCode === 1600;
  if (res.status !== 401 && !isMerchantRequired) showToast(errorMessage);
  if (isMerchantRequired) useTenantStore.getState().openSelector();

  throw {
    status: res.status,
    message: errorMessage,
    errors: errorData.errors,
    errorCode: errorData.errorCode,
    fullError: errorData,
    toasted: true,
  };
}

async function request<T = any>(
  method: string,
  endpoint: string,
  data?: any,
  isFormData?: boolean
): Promise<T> {
  const { url, headers } = buildRequest(endpoint, "application/json");

  if (!isFormData && data && method !== "GET") {
    headers["Content-Type"] = "application/json";
  }

  const config: RequestInit = {
    method,
    headers,
  };

  if (data && method !== "GET") {
    config.body = isFormData ? data : JSON.stringify(data);
  }

  const res = await fetch(url, config);

  if (!res.ok) {
    await throwResponseError(res, "Something went wrong");
  }
  return readJsonBody<T>(res);
}

/**
 * GET a resource that may legitimately not exist yet (e.g. a contact's outreach brand card
 * before anyone generated one). A 404 resolves to `null` WITHOUT a toast — the page renders
 * an empty state instead of flashing an error on every visit. Any other failure keeps the
 * normal `request` handling.
 */
async function requestOrNull<T = any>(endpoint: string): Promise<T | null> {
  const { url, headers } = buildRequest(endpoint, "application/json");
  const res = await fetch(url, { method: "GET", headers });
  if (res.status === 404) return null;
  if (!res.ok) {
    await throwResponseError(res, "Something went wrong");
  }
  return readJsonBody<T>(res);
}

/**
 * Parse a 2xx body, tolerating the ones that have no body at all.
 *
 * A bare `res.json()` throws on 204 No Content, which several BFF routes return
 * (`DELETE /feature-flags/:id`, the branch-exclusion writes, watchman's 2FA
 * disable/reset). That turned a successful mutation into a rejected promise and
 * an error toast. 205 and a zero-length Content-Length are the same shape, and a
 * body that arrives empty despite the headers is treated the same way rather
 * than surfacing a SyntaxError the caller cannot act on.
 */
async function readJsonBody<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.status === 205 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/**
 * Binary/file download against the same BFF, sharing `request`'s auth, tenant and
 * error semantics — but reading the body as a Blob instead of JSON.
 *
 * `request` unconditionally ends in `res.json()`, so it cannot be used for the
 * endpoints that stream a file: the sales-invoice PDFs (PE-2143) and the
 * platform-wide CSV/Excel exports (PE-2127). Rather than each feature
 * hand-rolling a `fetch` (and quietly dropping the bearer, the x-tenant-id or the
 * 401 logout), they go through here.
 *
 * The filename prefers the server's Content-Disposition and falls back to
 * `fallbackFilename`, so a backend rename doesn't silently produce "download".
 */
async function downloadRequest(endpoint: string, fallbackFilename: string): Promise<void> {
  // Accept anything: exports negotiate CSV vs Excel, and PDFs are not JSON.
  const { url, headers } = buildRequest(endpoint, "*/*");

  const res = await fetch(url, { method: "GET", headers });

  if (!res.ok) {
    await throwResponseError(res, "Download failed");
  }

  const disposition = res.headers.get('content-disposition') || '';
  // RFC 5987 `filename*=UTF-8''name` first, then the plain quoted/bare `filename=`.
  const extendedMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  let filename = fallbackFilename;
  if (extendedMatch?.[1]) {
    try {
      filename = decodeURIComponent(extendedMatch[1]);
    } catch {
      filename = fallbackFilename;
    }
  } else if (plainMatch?.[1]) {
    filename = plainMatch[1];
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

export const ApiFetch = {
  get: <T = any>(endpoint: string) => request<T>("GET", endpoint),
  /** GET that resolves `null` on a 404 instead of toasting. See requestOrNull. */
  getOrNull: <T = any>(endpoint: string) => requestOrNull<T>(endpoint),
  /** GET a file and save it. See downloadRequest — used by sales PDFs and data exports. */
  download: (endpoint: string, fallbackFilename: string) =>
    downloadRequest(endpoint, fallbackFilename),
  post: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("POST", endpoint, data, isFormData),
  put: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("PATCH", endpoint, data, isFormData),
  legacy_put: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("PUT", endpoint, data, isFormData),
  del: <T = any>(endpoint: string) => request<T>("DELETE", endpoint),
};
